import { formatDecimal } from '@api/useFormatter'
import { NumberCellEditor } from '@components/_SiteWide/Grid/CellEditors/NumberCellEditor'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { getStoreNumberComparator } from '@utils/grid'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { message, Popover, Tooltip } from 'antd'
import moment from 'moment/moment'
import type { ForecastDay } from '../../api/schema'
import {
  IconArrowNarrowDown,
  IconArrowNarrowUp,
  IconCalendarStar,
  IconCircleOff,
  IconRefresh,
} from '@tabler/icons-react'
import { cloneDeep } from 'lodash'

const formateDateAndTime = (date: string): string => {
  return moment.utc(date).local().format('MM/DD/YYYY hh:mm A')
}

const getAdjustmentIcon = (adjustmentAmount: number) => {
  if (adjustmentAmount > 0) {
    return <IconArrowNarrowUp color='green' className='override-icon arrow-up-icon' />
  }
  if (adjustmentAmount < 0) {
    return <IconArrowNarrowDown color='red' className='override-icon' />
  }
  return null
}

export function updateForecastValue(params, newValue, index, isExcludedFromForecast) {
  const existing = cloneDeep(params.data?.days[index])
  if (newValue === null && existing?.volume === null) {
    return false
  }
  if (newValue === null && existing?.volume !== null) {
    params.data.days[index] = {
      ...existing,
      delete_override: true,
      adjustment_percentage: null,
      volume: existing?.original_volume ?? existing?.volume,
      original_volume: null,
      is_sales_excluded: isExcludedFromForecast,
      is_dirty_edited: true,
    }
    return true
  }

  if (newValue > 1000000 || newValue < -1000000) {
    message.error('Volume must be between -1,000,000 and 1,000,000')
    return false
  }
  const volume = existing?.original_volume ?? existing?.volume
  params.data.days[index] = {
    ...existing,
    volume: newValue ?? volume,
    adjustment_amount: newValue - volume,
    adjustment_percentage: (newValue / (volume || 1)) * 100 - 100,
    original_volume: existing?.original_volume ?? existing?.volume,
    delete_override: false,
    is_sales_excluded: isExcludedFromForecast,
    is_dirty_edited: true,
  }
  return true
}

function CustomForecastOverrideCellRenderer({ data }: { data: ForecastDay }) {
  const isOverriden = isOverride('volume', data)
  const isExcludedFromForecast = data?.is_sales_excluded
  const hasEvent = !!data?.event_id

  function ellipsesText(event_name) {
    if (event_name?.length > 20) {
      return `${event_name.substring(0, 17).trim()}...`
    }
    return event_name
  }

  const isAdjusted = data?.adjustment_amount !== 0
  const adjustmentIcon = getAdjustmentIcon(data?.adjustment_amount)
  const formattedAdjustmentPercentage = Math.round(Math.abs(data?.adjustment_percentage))

  const popoverContent = (
    <>
      <Vertical className='popover-content'>
        {/* Forecast */}
        <Horizontal className='mb-2'>
          <Vertical flex={1}>
            <Texto weight={700}>Forecast</Texto>
            <Texto className='popover-text'>{formatDecimal(data?.volume, 0)}</Texto>
          </Vertical>
        </Horizontal>

        {/* Adjustment */}
        <Horizontal className='mb-2'>
          <Vertical flex={1}>
            <Texto weight={700}>Adjustment</Texto>
            <Texto className='popover-text forecast-text'>
              {adjustmentIcon && (
                <>
                  {adjustmentIcon}
                  <Texto className='base-text mr-1'>{`${formattedAdjustmentPercentage}%`}</Texto>
                </>
              )}
              {Math.abs(data?.adjustment_amount)}
            </Texto>
          </Vertical>
        </Horizontal>

        {/* Base */}
        <Horizontal className='mb-2'>
          <Vertical flex={1}>
            <Texto weight={700}>Base</Texto>
            <Texto className='popover-text base-text'>{formatDecimal(data?.original_volume, 0)}</Texto>
          </Vertical>
        </Horizontal>

        {/* Events */}
        {data?.event_name && (
          <Horizontal className='mb-2'>
            <Vertical flex={1}>
              <Horizontal alignItems='center' style={{ fontWeight: 700 }}>
                <IconCalendarStar size='18px' className='pr-1 pb-1' /> <Texto>Event</Texto>
              </Horizontal>
              <Texto className='popover-text'>{ellipsesText(data?.event_name)}</Texto>
            </Vertical>
          </Horizontal>
        )}

        {/* Exclusions */}
        {isExcludedFromForecast && (
          <Horizontal className='mb-2'>
            <Vertical flex={1}>
              <Texto weight={700}>Exclusions</Texto>
              <Texto className='popover-text'>
                <IconCircleOff className='override-icon mr-1' />
                Forecast
              </Texto>
            </Vertical>
          </Horizontal>
        )}

        {/* Date/Time */}
        <Horizontal className='mb-2'>
          <Vertical flex={1}>
            <Texto weight={700}>Date/Time</Texto>
            <Texto className='popover-text'>{formateDateAndTime(data?.adjusted_on)}</Texto>
          </Vertical>
        </Horizontal>

        {/* User */}
        <Horizontal className='mb-1'>
          <Vertical flex={1}>
            <Texto weight={700}>User</Texto>
            <Texto className='popover-text'>{data?.updated_by}</Texto>
          </Vertical>
        </Horizontal>
      </Vertical>
    </>
  )

  if (isOverriden) {
    return (
      <Popover overlayClassName='override-popover' trigger={data.updated_by ? 'hover' : ''} content={popoverContent}>
        <span className='overridden-cell-container'>
          <Horizontal justifyContent='end'>
            <Vertical>
              <Horizontal verticalCenter justifyContent='end'>
                {isExcludedFromForecast && <IconCircleOff className='override-icon' />}
                {hasEvent && <IconCalendarStar className='override-icon' />}
                <Texto className='overridden-cell-text pl-1'>{formatDecimal(data?.volume, 0)}</Texto>
              </Horizontal>
              <Horizontal verticalCenter justifyContent='end'>
                {adjustmentIcon}
                {isAdjusted && <Texto>{formattedAdjustmentPercentage}%</Texto>}
              </Horizontal>
            </Vertical>
          </Horizontal>
        </span>
      </Popover>
    )
  }
  return formatDecimal(data?.volume, 0)
}

function isOverride(field: 'volume' | 'original_volume', data: ForecastDay, extraParams?: ICellRendererParams) {
  if (extraParams?.node?.group) {
    return false
  }
  return field === 'volume' && data?.original_volume !== null
}

function isDelete(field, data: ForecastDay & { delete_override: boolean }) {
  return field === 'volume' && data?.delete_override
}

function CustomGroupCellRenderer({ data }: { data: ForecastDay[] }) {
  const totalNegativeAdjustments = data.filter(
    (day) => day.adjustment_amount < 0 && day.original_volume !== null
  ).length
  const totalPositiveAdjustments = data.filter(
    (day) => day.adjustment_amount >= 0 && day.original_volume !== null
  ).length
  const totalExcludedFromForecast = data.filter((day) => day.is_sales_excluded).length

  return (
    <span className='group-cell-container'>
      {!!totalNegativeAdjustments && (
        <Horizontal verticalCenter>
          <Texto>{totalNegativeAdjustments}</Texto>
          <IconArrowNarrowDown color='red' className='override-icon' />
        </Horizontal>
      )}
      {!!totalPositiveAdjustments && (
        <Horizontal verticalCenter>
          <Texto>{totalPositiveAdjustments}</Texto>
          <IconArrowNarrowUp color='green' className='override-icon arrow-up-icon' />
        </Horizontal>
      )}
      {!!totalExcludedFromForecast && (
        <Horizontal verticalCenter>
          <Texto>{totalExcludedFromForecast}</Texto>
          <IconCircleOff className='override-icon' />
        </Horizontal>
      )}
    </span>
  )
}

function getEditableColumnConfigs(
  field: 'volume' | 'original_volume',
  index: number,
  forecastOverrideStatus,
  dirtyRef,
  isExcludedFromForecast: boolean
) {
  return {
    editable: field === 'volume' && true,
    suppressMovable: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    enableRowGroup: false,
    field: `${field}_${index}`,
    cellRenderer: (params: ICellRendererParams) => {
      if (params.node.group) {
        if (field === 'original_volume') return
        const data = params.node.allLeafChildren.flat().map((node) => node.data.days[index])
        return <CustomGroupCellRenderer data={data} />
      }
      const isOverriden = isOverride(field, params.data?.days[index])
      if (!isOverriden) {
        return params.value !== null ? (
          <Horizontal justifyContent='end'>{formatDecimal(params.value, 0)}</Horizontal>
        ) : (
          ''
        )
      }
      return <CustomForecastOverrideCellRenderer data={params.data?.days[index]} />
    },
    cellClassRules: {
      'overridden-cell': (params) => {
        if (field === 'original_volume' || params.node.group) return false
        if (isOverride(field, params.data?.days[index], params)) return true
      },
      'delete-cell': (params) => isDelete(field, params.data?.days[index]),
    },
    cellEditor: NumberCellEditor,
    filter: 'agNumberColumnFilter',
    cellEditorParams: {
      minValue: 0,
    },
    cellStyle: (params) => {
      return lockedCellStyle(params, forecastOverrideStatus)
    },

    valueSetter: (params) => updateForecastValue(params, params.newValue, index, isExcludedFromForecast),
  }
}

const lockedCellStyle = (params, forecastOverrideStatus) => {
  if (!params.data) return {}
  const store_tank = `${params.data.store_number}|${params.data.tank_id}`
  if (!forecastOverrideStatus?.progress?.[store_tank]) return {}
  return {
    opacity: 0.5,
  }
}

export const getColumnDefs = (
  dates: string[],
  forecastOverrideStatus,
  dirtyRef,
  isExcludedFromForecast: boolean
): ColDef[] => {
  const staticColumns: ColDef[] = [
    { headerName: 'State', field: 'state', rowGroup: true, hide: true },
    {
      headerName: 'Site',
      field: 'store_number',
      rowGroup: true,
      hide: true,
      comparator: getStoreNumberComparator('store_number'),
    },
    {
      headerName: '',
      field: 'is_locked',
      valueGetter: (params) => {
        if (!params.data) return null
        const store_tank = `${params.data.store_number}|${params.data.tank_id}`
        if (forecastOverrideStatus?.progress?.[store_tank]) {
          return forecastOverrideStatus.progress[store_tank]
        }
        return null
      },
      cellRenderer: (params) => {
        if (params.value === null) return null

        return (
          <Tooltip
            title={
              <Vertical style={{ color: 'white' }}>
                <Horizontal>
                  <Texto appearance='white'>
                    Forecast Pending. Updated at {moment.utc(params.value?.initiated_at).local().format('h:mma')} by{' '}
                    {params.value?.initiated_by}
                  </Texto>
                </Horizontal>
              </Vertical>
            }
            placement='right'
            style={{ maxWidth: '200px', width: '200px', color: 'white' }}
          >
            <div className='refresh-icon'>
              <IconRefresh size={24} />
            </div>
          </Tooltip>
        )
      },
      suppressFiltersToolPanel: true,
      suppressColumnsToolPanel: true,
      lockVisible: true,
      enableRowGroup: false,
      suppressMovable: true,
      width: 56,
      minWidth: 56,
      maxWidth: 56,
      menuTabs: [],
    },
    {
      headerName: 'Market',
      field: 'market',
      cellStyle: (params) => {
        return lockedCellStyle(params, forecastOverrideStatus)
      },
    },
    {
      headerName: 'Sector',
      field: 'sector',
      cellStyle: (params) => {
        return lockedCellStyle(params, forecastOverrideStatus)
      },
    },
    {
      headerName: 'Tank ID',
      field: 'tank_id',
      cellStyle: (params) => {
        return lockedCellStyle(params, forecastOverrideStatus)
      },
    },
    {
      headerName: 'Product',
      field: 'product',
      cellStyle: (params) => {
        return lockedCellStyle(params, forecastOverrideStatus)
      },
    },
  ]

  const columnDefs = [{ headerName: 'Tank Reference', children: staticColumns }]

  dates.forEach((date, idx) => {
    columnDefs.push({
      headerName: moment(date).format('MM/DD'),
      suppressMovable: true,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
      children: [
        {
          headerName: 'Base',
          field: 'original_volume',
          colId: `original_volume_${idx}`,
          type: 'rightAligned',
          hide: true,
          valueGetter: (params) => {
            return params.data?.days[idx]?.original_volume
          },
          ...getEditableColumnConfigs('original_volume', idx, forecastOverrideStatus, dirtyRef, isExcludedFromForecast),
        },
        {
          $isCellDirty: (params) => params.data?.days?.[idx]?.is_dirty_edited,
          headerName: 'Forecast',
          field: 'volume',
          colId: `volume_${idx}`,
          valueGetter: (params) => {
            return params.data?.days[idx]?.volume
          },
          ...getEditableColumnConfigs('volume', idx, forecastOverrideStatus, dirtyRef, isExcludedFromForecast),
        },
      ],
    })
  })
  return columnDefs
}
